/**
 * @voltxampere/persistence — Recents ring (Sprint 21)
 *
 * Public API:
 *
 *   pushRecent(id, name)  — record a circuit open; shifts older
 *                           entries down the ring; trims to `size`
 *   listRecents()         — slot-ordered (0 = most recent)
 *   clearRecents()        — wipe the ring
 *   removeRecent(id)      — drop a single entry (used when the
 *                           underlying circuit is deleted)
 *
 * Ring size defaults to 8 (Sprint 21 D-21-6) but is configurable
 * via the `setRecentsSize` setter (used by AYARLAR if Sprint 22+
 * adds a "Recents count" preference).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { openDb, requestToPromise, transactionDone } from "./db.js";
import { DEFAULT_RECENTS_SIZE, STORE_RECENTS } from "./types.js";
let ringSize = DEFAULT_RECENTS_SIZE;
export function setRecentsSize(size) {
    if (!Number.isInteger(size) || size < 1 || size > 100) {
        throw new Error(`setRecentsSize: size must be 1..100, got ${String(size)}`);
    }
    ringSize = size;
}
export function getRecentsSize() {
    return ringSize;
}
/**
 * Mark a circuit as the most-recently opened. Existing entries
 * shift one slot down; if the same circuit was already in the
 * ring, its old slot is removed before the new slot 0 is written
 * (so we never duplicate ids).
 */
export async function pushRecent(id, name, now = Date.now()) {
    const db = await openDb();
    const tx = db.transaction(STORE_RECENTS, "readwrite");
    const store = tx.objectStore(STORE_RECENTS);
    // 1. Read all existing entries (≤ ring size, fast).
    const cursorReq = store.openCursor();
    const existing = [];
    await new Promise((resolve, reject) => {
        cursorReq.onsuccess = () => {
            const c = cursorReq.result;
            if (!c) {
                resolve();
                return;
            }
            existing.push(c.value);
            c.continue();
        };
        cursorReq.onerror = () => {
            reject(cursorReq.error ?? new Error("pushRecent: cursor failed"));
        };
    });
    // 2. Remove any prior entry with the same id (dedupe).
    const filtered = existing.filter((e) => e.id !== id);
    // 3. Sort by current slot ascending so we shift correctly.
    filtered.sort((a, b) => a.slot - b.slot);
    // 4. Wipe the store; we'll rewrite the ring with the new ordering.
    await requestToPromise(store.clear());
    // 5. Write slot 0 = new entry; slots 1..size-1 = filtered shifted by 1.
    const head = { slot: 0, id, name, openedAt: now };
    await requestToPromise(store.put(head));
    for (let i = 0; i < filtered.length && i + 1 < ringSize; i++) {
        const old = filtered[i];
        if (old === undefined)
            continue;
        const shifted = {
            slot: i + 1,
            id: old.id,
            name: old.name,
            openedAt: old.openedAt,
        };
        await requestToPromise(store.put(shifted));
    }
    await transactionDone(tx);
}
export async function listRecents() {
    const db = await openDb();
    const tx = db.transaction(STORE_RECENTS, "readonly");
    const store = tx.objectStore(STORE_RECENTS);
    const cursorReq = store.openCursor(); // ascending by slot (primary key)
    const out = [];
    await new Promise((resolve, reject) => {
        cursorReq.onsuccess = () => {
            const c = cursorReq.result;
            if (!c) {
                resolve();
                return;
            }
            out.push(c.value);
            c.continue();
        };
        cursorReq.onerror = () => {
            reject(cursorReq.error ?? new Error("listRecents: cursor failed"));
        };
    });
    await transactionDone(tx);
    return out;
}
export async function clearRecents() {
    const db = await openDb();
    const tx = db.transaction(STORE_RECENTS, "readwrite");
    const store = tx.objectStore(STORE_RECENTS);
    await requestToPromise(store.clear());
    await transactionDone(tx);
}
export async function removeRecent(id) {
    const db = await openDb();
    const tx = db.transaction(STORE_RECENTS, "readwrite");
    const store = tx.objectStore(STORE_RECENTS);
    const cursorReq = store.openCursor();
    const remaining = [];
    await new Promise((resolve, reject) => {
        cursorReq.onsuccess = () => {
            const c = cursorReq.result;
            if (!c) {
                resolve();
                return;
            }
            const entry = c.value;
            if (entry.id !== id)
                remaining.push(entry);
            c.continue();
        };
        cursorReq.onerror = () => {
            reject(cursorReq.error ?? new Error("removeRecent: cursor failed"));
        };
    });
    // Reset and re-write to keep slots contiguous.
    await requestToPromise(store.clear());
    remaining.sort((a, b) => a.slot - b.slot);
    for (let i = 0; i < remaining.length; i++) {
        const r = remaining[i];
        if (r === undefined)
            continue;
        await requestToPromise(store.put({ slot: i, id: r.id, name: r.name, openedAt: r.openedAt }));
    }
    await transactionDone(tx);
}
//# sourceMappingURL=recents-store.js.map